import AiCloserAPI from '../../api/aiCloser';
// Same toast channel useAlert() emits on; imported directly, as other store modules
// do, so the store takes no dependency on the composables layer.
import { emitter } from 'shared/helpers/mitt';

const state = {
  config: null,
  product: null,
  products: [],
  answerTemplates: [],
  answerTemplatesMeta: { total: 0, limit: 50, offset: 0 },
  prices: [],
  media: [],
  mediaMeta: { total: 0, limit: 25, offset: 0 },
  links: [],
  linksMeta: { total: 0, limit: 50, offset: 0 },
  testimonials: [],
  certificates: [],
  uiFlags: {
    isFetchingConfig: false,
    isUpdatingConfig: false,
    isFetchingProduct: false,
    isUpdatingProduct: false,
    isFetchingProducts: false,
    isFetchingAnswerTemplates: false,
    isFetchingPrices: false,
    isFetchingMedia: false,
    isFetchingLinks: false,
    isFetchingTestimonials: false,
    isFetchingCertificates: false,
  },
};

const getters = {
  getConfig: $state => $state.config,
  getProduct: $state => $state.product,
  getProducts: $state => $state.products,
  getAnswerTemplates: $state => $state.answerTemplates,
  getAnswerTemplatesMeta: $state => $state.answerTemplatesMeta,
  getPrices: $state => $state.prices,
  getMedia: $state => $state.media,
  getMediaMeta: $state => $state.mediaMeta,
  getLinks: $state => $state.links,
  getLinksMeta: $state => $state.linksMeta,
  getTestimonials: $state => $state.testimonials,
  getCertificates: $state => $state.certificates,
  getUIFlags: $state => $state.uiFlags,
};

const actions = {
  // Config Actions
  async fetchConfig({ commit }, { assistantId }) {
    commit('setUIFlag', { isFetchingConfig: true });
    try {
      const response = await AiCloserAPI.getConfig(assistantId);
      commit('setConfig', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingConfig: false });
    }
  },

  async updateConfig({ commit }, { assistantId, data }) {
    commit('setUIFlag', { isUpdatingConfig: true });
    try {
      const response = await AiCloserAPI.updateConfig(assistantId, data);
      // The save succeeded but did not reach the runtime (AI Studio / AI Gamer
      // auto-publish). Raised here rather than in each of the ~22 screens that save
      // through this action, so none of them can report "saved" and stop there.
      const { publish_warning: publishWarning, ...config } =
        response.data || {};
      commit('setConfig', config);
      if (publishWarning) {
        emitter.emit('newToastMessage', {
          message: `⚠️ ${publishWarning}`,
          action: null,
        });
      }
      return config;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isUpdatingConfig: false });
    }
  },

  // Product Actions
  async fetchProduct({ commit }, { assistantId }) {
    commit('setUIFlag', { isFetchingProduct: true });
    try {
      const response = await AiCloserAPI.getProduct(assistantId);
      commit('setProduct', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingProduct: false });
    }
  },

  async updateProduct({ commit }, { assistantId, data }) {
    commit('setUIFlag', { isUpdatingProduct: true });
    try {
      const response = await AiCloserAPI.updateProduct(assistantId, data);
      commit('setProduct', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isUpdatingProduct: false });
    }
  },

  // Multiple Products Actions
  async fetchProducts({ commit }, { assistantId }) {
    commit('setUIFlag', { isFetchingProducts: true });
    try {
      const response = await AiCloserAPI.getProducts(assistantId);
      commit('setProducts', response.data.products || []);
      return response.data.products;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingProducts: false });
    }
  },

  async createProduct({ commit, state: $state }, { assistantId, data }) {
    try {
      const response = await AiCloserAPI.createProduct(assistantId, data);
      commit('setProducts', [...$state.products, response.data]);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async updateProductById(
    { commit, state: $state },
    { assistantId, productId, data }
  ) {
    try {
      const response = await AiCloserAPI.updateProductById(
        assistantId,
        productId,
        data
      );
      const index = $state.products.findIndex(p => p.id === productId);
      if (index !== -1) {
        const updated = [...$state.products];
        updated[index] = response.data;
        commit('setProducts', updated);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async deleteProduct({ commit, state: $state }, { assistantId, productId }) {
    try {
      await AiCloserAPI.deleteProduct(assistantId, productId);
      commit(
        'setProducts',
        $state.products.filter(p => p.id !== productId)
      );
    } catch (error) {
      throw error;
    }
  },

  // Answer Templates Actions
  async fetchAnswerTemplates(
    { commit, state: $state },
    { assistantId, params = {}, append = false }
  ) {
    commit('setUIFlag', { isFetchingAnswerTemplates: true });
    try {
      const response = await AiCloserAPI.getAnswerTemplates(
        assistantId,
        params
      );
      const items = response.data.data || [];
      commit(
        'setAnswerTemplatesMeta',
        response.data.meta || { total: items.length, limit: 50, offset: 0 }
      );

      if (!append) {
        commit('setAnswerTemplates', items);
        return items;
      }

      // Append unique-by-id
      const byId = new Map(($state.answerTemplates || []).map(t => [t.id, t]));
      items.forEach(t => byId.set(t.id, t));
      const merged = Array.from(byId.values());
      commit('setAnswerTemplates', merged);
      return merged;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingAnswerTemplates: false });
    }
  },

  async createAnswerTemplate({ commit, state: $state }, { assistantId, data }) {
    try {
      const response = await AiCloserAPI.createAnswerTemplate(
        assistantId,
        data
      );
      commit('setAnswerTemplates', [response.data, ...$state.answerTemplates]);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async updateAnswerTemplate(
    { commit, state: $state },
    { assistantId, templateId, data }
  ) {
    try {
      const response = await AiCloserAPI.updateAnswerTemplate(
        assistantId,
        templateId,
        data
      );
      const index = $state.answerTemplates.findIndex(t => t.id === templateId);
      if (index !== -1) {
        const updated = [...$state.answerTemplates];
        updated[index] = response.data;
        commit('setAnswerTemplates', updated);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async deleteAnswerTemplate(
    { commit, state: $state },
    { assistantId, templateId }
  ) {
    try {
      await AiCloserAPI.deleteAnswerTemplate(assistantId, templateId);
      commit(
        'setAnswerTemplates',
        $state.answerTemplates.filter(t => t.id !== templateId)
      );
    } catch (error) {
      throw error;
    }
  },

  async autoTagAnswerTemplates({ commit }, { assistantId, options = {} }) {
    try {
      const response = await AiCloserAPI.autoTagAnswerTemplates(
        assistantId,
        options
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Prices Actions
  async fetchPrices({ commit }, { assistantId, params = {} }) {
    commit('setUIFlag', { isFetchingPrices: true });
    try {
      const response = await AiCloserAPI.getPrices(assistantId, params);
      commit('setPrices', response.data.data || []);
      return response.data.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingPrices: false });
    }
  },

  async createPrice({ commit, state: $state }, { assistantId, data }) {
    try {
      const response = await AiCloserAPI.createPrice(assistantId, data);
      commit('setPrices', [...$state.prices, response.data]);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async updatePrice({ commit, state: $state }, { assistantId, priceId, data }) {
    try {
      const response = await AiCloserAPI.updatePrice(
        assistantId,
        priceId,
        data
      );
      const index = $state.prices.findIndex(p => p.id === priceId);
      if (index !== -1) {
        const updated = [...$state.prices];
        updated[index] = response.data;
        commit('setPrices', updated);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async deletePrice({ commit, state: $state }, { assistantId, priceId }) {
    try {
      await AiCloserAPI.deletePrice(assistantId, priceId);
      commit(
        'setPrices',
        $state.prices.filter(p => p.id !== priceId)
      );
    } catch (error) {
      throw error;
    }
  },

  // Media Actions
  async fetchMedia({ commit }, { assistantId, params = {} }) {
    commit('setUIFlag', { isFetchingMedia: true });
    try {
      const response = await AiCloserAPI.getMedia(assistantId, params);
      commit('setMedia', response.data.data || []);
      commit(
        'setMediaMeta',
        response.data.meta || {
          total: (response.data.data || []).length,
          limit: 25,
          offset: 0,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingMedia: false });
    }
  },

  async createMedia({ commit, state: $state }, { assistantId, data }) {
    try {
      const response = await AiCloserAPI.createMedia(assistantId, data);
      commit('setMedia', [response.data, ...$state.media]);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async updateMedia({ commit, state: $state }, { assistantId, mediaId, data }) {
    try {
      const response = await AiCloserAPI.updateMedia(
        assistantId,
        mediaId,
        data
      );
      const index = $state.media.findIndex(m => m.id === mediaId);
      if (index !== -1) {
        const updated = [...$state.media];
        updated[index] = response.data;
        commit('setMedia', updated);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async deleteMedia({ commit, state: $state }, { assistantId, mediaId }) {
    try {
      await AiCloserAPI.deleteMedia(assistantId, mediaId);
      commit(
        'setMedia',
        $state.media.filter(m => m.id !== mediaId)
      );
    } catch (error) {
      throw error;
    }
  },

  // Links Actions (AI gamer)
  async fetchLinks({ commit }, { assistantId, params = {} }) {
    commit('setUIFlag', { isFetchingLinks: true });
    try {
      const response = await AiCloserAPI.getLinks(assistantId, params);
      commit('setLinks', response.data.data || []);
      commit(
        'setLinksMeta',
        response.data.meta || {
          total: (response.data.data || []).length,
          limit: 50,
          offset: 0,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingLinks: false });
    }
  },

  async createLink({ commit, state: $state }, { assistantId, data }) {
    try {
      const response = await AiCloserAPI.createLink(assistantId, data);
      commit('setLinks', [response.data, ...$state.links]);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async updateLink({ commit, state: $state }, { assistantId, linkId, data }) {
    try {
      const response = await AiCloserAPI.updateLink(assistantId, linkId, data);
      const index = $state.links.findIndex(l => l.id === linkId);
      if (index !== -1) {
        const updated = [...$state.links];
        updated[index] = response.data;
        commit('setLinks', updated);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async deleteLink({ commit, state: $state }, { assistantId, linkId }) {
    try {
      await AiCloserAPI.deleteLink(assistantId, linkId);
      commit(
        'setLinks',
        $state.links.filter(l => l.id !== linkId)
      );
    } catch (error) {
      throw error;
    }
  },

  // Testimonials Actions
  async fetchTestimonials({ commit }, { assistantId }) {
    commit('setUIFlag', { isFetchingTestimonials: true });
    try {
      const response = await AiCloserAPI.getTestimonials(assistantId);
      commit('setTestimonials', response.data.data || []);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingTestimonials: false });
    }
  },

  async createTestimonial({ commit, state: $state }, { assistantId, data }) {
    try {
      const response = await AiCloserAPI.createTestimonial(assistantId, data);
      commit('setTestimonials', [response.data, ...$state.testimonials]);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async updateTestimonial(
    { commit, state: $state },
    { assistantId, testimonialId, data }
  ) {
    try {
      const response = await AiCloserAPI.updateTestimonial(
        assistantId,
        testimonialId,
        data
      );
      const index = $state.testimonials.findIndex(t => t.id === testimonialId);
      if (index !== -1) {
        const updated = [...$state.testimonials];
        updated[index] = response.data;
        commit('setTestimonials', updated);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async deleteTestimonial(
    { commit, state: $state },
    { assistantId, testimonialId }
  ) {
    try {
      await AiCloserAPI.deleteTestimonial(assistantId, testimonialId);
      commit(
        'setTestimonials',
        $state.testimonials.filter(t => t.id !== testimonialId)
      );
    } catch (error) {
      throw error;
    }
  },

  // Certificates Actions
  async fetchCertificates({ commit }, { assistantId }) {
    commit('setUIFlag', { isFetchingCertificates: true });
    try {
      const response = await AiCloserAPI.getCertificates(assistantId);
      commit('setCertificates', response.data.data || []);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingCertificates: false });
    }
  },

  async createCertificate({ commit, state: $state }, { assistantId, data }) {
    try {
      const response = await AiCloserAPI.createCertificate(assistantId, data);
      commit('setCertificates', [response.data, ...$state.certificates]);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async updateCertificate(
    { commit, state: $state },
    { assistantId, certificateId, data }
  ) {
    try {
      const response = await AiCloserAPI.updateCertificate(
        assistantId,
        certificateId,
        data
      );
      const index = $state.certificates.findIndex(c => c.id === certificateId);
      if (index !== -1) {
        const updated = [...$state.certificates];
        updated[index] = response.data;
        commit('setCertificates', updated);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async deleteCertificate(
    { commit, state: $state },
    { assistantId, certificateId }
  ) {
    try {
      await AiCloserAPI.deleteCertificate(assistantId, certificateId);
      commit(
        'setCertificates',
        $state.certificates.filter(c => c.id !== certificateId)
      );
    } catch (error) {
      throw error;
    }
  },

  // Clear state
  clearState({ commit }) {
    commit('setConfig', null);
    commit('setProduct', null);
    commit('setProducts', []);
    commit('setAnswerTemplates', []);
    commit('setPrices', []);
    commit('setMedia', []);
    commit('setLinks', []);
    commit('setTestimonials', []);
    commit('setCertificates', []);
  },
};

const mutations = {
  setConfig($state, config) {
    // Normalize config payload across publish/draft flows.
    // Backend can return:
    // - `prompt_sections`: current editable draft (may be empty)
    // - `runtime_prompt_sections`: effective published snapshot
    // To prevent empty UI states, merge runtime as defaults and let draft override.
    if (config && typeof config === 'object' && !Array.isArray(config)) {
      const isPlainObject = v =>
        v !== null && typeof v === 'object' && !Array.isArray(v);

      const draft = isPlainObject(config.prompt_sections)
        ? config.prompt_sections
        : {};
      const runtime = isPlainObject(config.runtime_prompt_sections)
        ? config.runtime_prompt_sections
        : {};

      config.prompt_sections = { ...runtime, ...draft };
    }

    $state.config = config;
  },
  setProduct($state, product) {
    $state.product = product;
  },
  setProducts($state, products) {
    $state.products = products;
  },
  setAnswerTemplates($state, templates) {
    $state.answerTemplates = templates;
  },
  setAnswerTemplatesMeta($state, meta) {
    $state.answerTemplatesMeta = meta;
  },
  setPrices($state, prices) {
    $state.prices = prices;
  },
  setMedia($state, media) {
    $state.media = media;
  },
  setMediaMeta($state, meta) {
    $state.mediaMeta = meta;
  },
  setLinks($state, links) {
    $state.links = links;
  },
  setLinksMeta($state, meta) {
    $state.linksMeta = meta;
  },
  setTestimonials($state, testimonials) {
    $state.testimonials = testimonials;
  },
  setCertificates($state, certificates) {
    $state.certificates = certificates;
  },
  setUIFlag($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
